import { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, AlertTriangle, Info, TrendingUp, Sparkles, ArrowUpRight, Lightbulb } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const insightIcons = {
  success: { icon: CheckCircle, bg: 'bg-emerald-100', color: 'text-emerald-600', border: 'border-emerald-200', gradient: 'from-emerald-500 to-teal-500' },
  warning: { icon: AlertTriangle, bg: 'bg-amber-100', color: 'text-amber-600', border: 'border-amber-200', gradient: 'from-amber-500 to-orange-500' },
  info: { icon: Info, bg: 'bg-blue-100', color: 'text-blue-600', border: 'border-blue-200', gradient: 'from-blue-500 to-indigo-500' }
};

export const Insights = () => {
  const [insights, setInsights] = useState([]);
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [insightsRes, seoRes] = await Promise.all([
          axios.get(`${API}/insights`),
          axios.get(`${API}/analytics/seo`)
        ]);
        setInsights(insightsRes.data);
        setSeoData(seoRes.data);
      } catch (error) {
        console.error('Error fetching insights:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="insights-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-purple-500" />
              AI-Powered Insights
            </h1>
            <p className="text-slate-500 mt-1">Actionable recommendations based on your GiriUK data</p>
          </div>
        </div>

        {/* Summary Card */}
        <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg overflow-hidden" data-testid="insights-summary">
          <CardContent className="p-6 relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <TrendingUp className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">January 2026: Strong Recovery Month</h3>
                <p className="text-white/80 mt-1">
                  After a slower December, your metrics rebounded significantly. Instagram reach grew 162%, 
                  Facebook reach jumped 256%, and new followers increased substantially across both platforms.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Insights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insights.map((insight, index) => {
            const style = insightIcons[insight.type] || insightIcons.info;
            const IconComponent = style.icon;
            return (
              <Card 
                key={insight.id} 
                className={`bg-white shadow-sm border-l-4 ${style.border} card-hover cursor-pointer group`}
                data-testid={`insight-card-${insight.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <div className={`p-2 ${style.bg} rounded-lg h-fit group-hover:scale-110 transition-transform`}>
                      <IconComponent className={`h-5 w-5 ${style.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-slate-800">{insight.title}</h3>
                        <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-purple-500 transition-colors" />
                      </div>
                      <p className="text-sm text-slate-600 mt-2">{insight.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* SEO Keywords Section */}
        {seoData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Improved Keywords */}
            <Card className="bg-white shadow-sm" data-testid="improved-keywords">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  SEO Wins - Improved Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {seoData.improved.map((keyword) => (
                    <div key={keyword.keyword} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                      <span className="font-medium text-slate-700">{keyword.keyword}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">#{keyword.nov_rank || 'N/A'}</span>
                        <ArrowUpRight className="h-4 w-4 text-emerald-500" />
                        <Badge className="bg-emerald-100 text-emerald-700">#{keyword.dec_rank}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* New Rankings */}
            <Card className="bg-white shadow-sm" data-testid="new-rankings">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  New Keyword Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {seoData.new_rankings.map((keyword) => (
                    <div key={keyword.keyword} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <span className="font-medium text-slate-700">{keyword.keyword}</span>
                      <Badge className="bg-purple-100 text-purple-700">New at #{keyword.dec_rank}</Badge>
                    </div>
                  ))}
                  {seoData.new_rankings.length === 0 && (
                    <p className="text-slate-500 text-sm">No new rankings this period</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Items */}
        <Card className="bg-white shadow-sm" data-testid="action-items">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Recommended Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg hover:shadow-md transition-shadow cursor-pointer group">
                <h4 className="font-medium text-slate-800 group-hover:text-purple-600 transition-colors">Double Down on Reels</h4>
                <p className="text-sm text-slate-500 mt-2">Thaipusam Reel had highest engagement (2,340). Create more festival-themed reels.</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg hover:shadow-md transition-shadow cursor-pointer group">
                <h4 className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors">Expand UK Content</h4>
                <p className="text-sm text-slate-500 mt-2">Only 16.6% traffic from UK. Create UK-specific landing pages and content.</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg hover:shadow-md transition-shadow cursor-pointer group">
                <h4 className="font-medium text-slate-800 group-hover:text-emerald-600 transition-colors">Replicate Email Success</h4>
                <p className="text-sm text-slate-500 mt-2">Festival Essentials email had 39.66% CTR. Use similar format for upcoming festivals.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Insights;
